// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useEtlProcessingDetailQuery } from "@/api/hooks/use-imports";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImportDataDialog } from "@/features/imports/import-data-dialog";
import { ToggleEtlActiveDialog } from "@/features/ingress/toggle-etl-active-dialog";
import { TransformationActionsMenu } from "@/features/ingress/transformation-actions-menu";
import { TransformationActivityTab } from "@/features/ingress/transformation-activity-tab";
import { TransformationFilesTab } from "@/features/ingress/transformation-files-tab";
import { TransformationInfoTab } from "@/features/ingress/transformation-info-tab";
import { TransformationSqlTab } from "@/features/ingress/transformation-sql-tab";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

export const Route = createFileRoute("/_authenticated/ingress/transformations/$transformationId")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.ingressTransformations}>
      <TransformationDetailPage />
    </RequirePermission>
  ),
});

function TransformationDetailPage() {
  const { transformationId } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { debugMode } = useDebugMode();
  // Activity log is work-in-progress — debug-mode only; fall back to Info when hidden.
  const activeTab = tab === "activity" && !debugMode ? "info" : (tab ?? "info");
  const navigate = useNavigate();
  const { t } = useTranslation("back-office");
  const { data, isLoading, isError } = useEtlProcessingDetailQuery(Number(transformationId));

  function handleTabChange(newTab: string) {
    navigate({
      to: "/ingress/transformations/$transformationId",
      params: { transformationId },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  const breadcrumbs = useMemo(() => [{ label: t("transformations.title"), to: "/ingress/transformations" }], [t]);
  const [toggleEtlOpen, setToggleEtlOpen] = useState(false);
  const [importDataOpen, setImportDataOpen] = useState(false);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <PageHeader title={`ETL #${transformationId}`} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={data.description}
        subtitle={
          <>
            <IdPill id={data.etlProcessingId} />
            <KeyChip label={data.shortName} />
            <StatusIndicator active={data.isActive} />
            <TransformationActionsMenu
              isActive={data.isActive}
              onToggleActive={() => setToggleEtlOpen(true)}
              onImportData={() => setImportDataOpen(true)}
              triggerClassName="ml-auto -my-1.5"
            />
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("transformations.detail.tabs.info")}</TabsTrigger>
              <TabsTrigger value="files">{t("transformations.detail.tabs.files")}</TabsTrigger>
              <TabsTrigger value="sql">{t("transformations.detail.tabs.sql")}</TabsTrigger>
              {debugMode && <TabsTrigger value="activity">{t("transformations.detail.tabs.activityLog")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <TransformationInfoTab etl={data} />
          </TabsContent>
          <TabsContent value="files" className="space-y-4">
            <TransformationFilesTab inputFiles={data.inputFiles} />
          </TabsContent>
          <TabsContent value="sql" className="space-y-4">
            <TransformationSqlTab sqlContent={data.sqlContent} />
          </TabsContent>
          {debugMode && (
            <TabsContent value="activity" className="space-y-4">
              <TransformationActivityTab
                transformationId={data.etlProcessingId}
                initialFilters={activeTab === "activity" ? f : undefined}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <ToggleEtlActiveDialog
        etl={
          toggleEtlOpen
            ? {
                etlProcessingId: data.etlProcessingId,
                description: data.description,
                isActive: data.isActive,
              }
            : null
        }
        onClose={() => setToggleEtlOpen(false)}
      />
      <ImportDataDialog open={importDataOpen} onOpenChange={setImportDataOpen} transformation={data} />
    </div>
  );
}
